import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import authService from '../services/auth/auth-service.js';
import useAuthStore from '../stores/auth-store.js';

export const useLogin = () => {
  const setAuth = useAuthStore((state) => state.setAuth);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);

  return useMutation({
    mutationFn: async (credentials) => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await authService.login(credentials);
        return data;
      } catch (error) {
        setLoading(false);
        throw error;
      }
    },

    onSuccess: (data) => {
      setAuth(data.token, data.user, data.permissions);
      setLoading(false);
    },

    onError: (error) => {
      setLoading(false);
      setError(error.message || 'Login failed');
    },
  });
};

export const useLogout = () => {
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const queryClient = useQueryClient();

  return () => {
    clearAuth();
    queryClient.clear();
  };
};

export const useClients = () => {
  const isSuperadmin = useAuthStore((state) => state.isSuperadmin);
  const setAvailableClients = useAuthStore((state) => state.setAvailableClients);

  return useQuery({
    queryKey: ['auth', 'clients'],
    queryFn: async () => {
      const clients = await authService.getClients();
      setAvailableClients(clients);
      return clients;
    },
    enabled: isSuperadmin(),
    staleTime: Infinity, // Clients rarely change
  });
};

export const useAppSettings = (appName, options = {}) => {
  const getEffectiveClientId = useAuthStore((state) => state.getEffectiveClientId);
  const effectiveClientId = getEffectiveClientId();
  
  const setAppSettings = useAuthStore((state) => state.setAppSettings);
  const setSettingsError = useAuthStore((state) => state.setSettingsError);
  const setLandingPage = useAuthStore((state) => state.setLandingPage);

  return useQuery({
    queryKey: ['auth', 'settings', appName, effectiveClientId],
    queryFn: async () => {
      try {
        const settings = await authService.getAppSettings(appName);
        
        // Special handling for 'auth' - extract and store landing page
        if (appName === 'auth') {
          const landingPagePath = settings?.landing?.landing_page;
          if (landingPagePath) {
            setLandingPage(landingPagePath);
            console.log('✅ Landing page extracted from auth settings:', landingPagePath);
          } else {
            console.warn('⚠️ No landing_page in auth settings response:', settings);
          }
        } else {
          // For app-specific settings (e.g., 'evacuation-portal'), store in appSettings
          setAppSettings(appName, settings);
        }
        
        return settings;
      } catch (error) {
        setSettingsError(appName, error.message || 'Failed to load settings');
        throw error;
      }
    },
    enabled: !!appName,
    staleTime: Infinity, // Settings are static - never go stale automatically, but allow manual invalidation
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    ...options,
  });
};
